<script lang="ts" setup>
  import logger from '@leaflink/snitch';
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Input from '@leaflink/stash/Input.vue';
  import Modal from '@leaflink/stash/Modal.vue';
  import RadioGroup from '@leaflink/stash/RadioGroup.vue';
  import RadioNew from '@leaflink/stash/RadioNew.vue';
  import useValidation from '@leaflink/stash/useValidation';
  import { ref } from 'vue';

  import { Strain, StrainClassification } from '@/models/inventory';
  import notify from '@/notify';
  import {
    AddStrainFormState,
    AddStrainValidationValues,
    getAddStrainValidationRules,
  } from '@/pages/Inventory/_schemas/validation';
  import { useInventoryStore } from '@/piniaStores/inventory/inventory.store';
  import i18n from '@/plugins/i18n';

  export interface AddStrainModalProps {
    open: boolean;
    classifications: StrainClassification[];
    companyId: number;
  }

  const props = defineProps<AddStrainModalProps>();

  const emit = defineEmits<{
    /**
     * Emitted when closing the modal
     */
    dismiss: [];

    /**
     * Emitted when a strain is created
     */
    submitted: [strain: Strain];
  }>();

  const { addStrain } = useInventoryStore();

  const isLoading = ref(false);

  const formState = ref<AddStrainFormState>(getInitialState());

  const validation = useValidation<AddStrainValidationValues>({
    rules: getAddStrainValidationRules(),
    values: formState,
  });

  function getInitialState(): AddStrainFormState {
    return {
      name: '',
      classification: '',
    };
  }

  async function submit() {
    await validation.validate({ preventScroll: true });

    if (validation.hasErrors) {
      return;
    }

    try {
      isLoading.value = true;

      const strain = await addStrain({
        name: formState.value.name,
        company: props.companyId,
        strainClassification: formState.value.classification,
      });

      emit('submitted', strain);
      closeModal({ forceClose: true });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logger.error(error);
      notify.error(i18n.t('notify.errorCustomerSupport'));
    } finally {
      isLoading.value = false;
    }
  }

  function closeModal({ forceClose }: { forceClose?: boolean } = {}) {
    if (!forceClose && isLoading.value) {
      return;
    }

    emit('dismiss');
    clearState();
  }

  function clearState() {
    formState.value = getInitialState();
    validation.setAllUntouched();
  }
</script>

<template>
  <Modal
    class="add-strain-modal"
    data-test="modal|add-strain-modal"
    :is-open="props.open"
    :title="i18n.t('inventory.strains.modal.addStrain.title')"
    @dismiss="closeModal()"
  >
    <Input
      v-model="formState.name"
      :label="i18n.t('inventory.strains.modal.addStrain.strainName')"
      :error-text="validation.getError('name')"
      class="add-strain-modal__strain-name-field"
      placeholder="Input text"
      type="text"
      name="name"
      add-bottom-space
      @blur="validation.touch('name')"
    />

    <RadioGroup
      v-model="formState.classification"
      :label="i18n.t('inventory.strains.strainClassification')"
      :error-text="validation.getError('classification')"
      class="[&>legend]:tw-mb-6"
      variant="chip"
      name="strain-classifications"
      @blur="validation.touch('classification')"
    >
      <RadioNew
        v-for="classification in props.classifications"
        :key="classification.value"
        :id="classification.value"
        :label="classification.displayValue"
        :value="classification.value"
      />
    </RadioGroup>

    <template #actions>
      <Button secondary @click="closeModal">{{ i18n.t('common.cancel') }}</Button>
      <Button :disabled="isLoading" @click="submit">
        <Icon v-if="isLoading" name="working" class="fx-spin" />
        <template v-else>
          {{ i18n.t('common.add') }}
        </template>
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
  /* TODO: remove these legacy page fixes after https://leaflink.atlassian.net/browse/CE-331 (Product Edit/Create Page Redesign) is complete */
  .add-strain-modal {
    @media screen('lg') {
      :deep(.stash-modal__dialog) {
        justify-content: center;
      }

      :deep(.stash-modal__body) {
        flex: unset;
      }
    }

    :deep(.stash-field) input::placeholder {
      opacity: 1;
    }

    :deep([role='radiogroup']) > .stash-label {
      border-bottom: 0;
    }
  }
</style>
